/**
 * Create an agent, as a panel form. An agent owns its capability allow-list,
 * so nothing can execute for the tenant until one exists. On submit the new
 * agent's record opens in the same panel.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { createAgent } from '@optum/api-client/endpoints/agents';

import { PanelFormActions, PanelFormFields, usePanelFormDone } from '../../components/shell/PanelForm.js';
import { Field } from '../../ui/Field.js';
import { Input } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { CapabilityMultiSelect } from './CapabilityMultiSelect.js';

export function NewAgentForm({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const done = usePanelFormDone();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [capabilities, setCapabilities] = useState<string[]>([]);

  const create = useMutation({
    mutationFn: () => createAgent({ organization_id: orgId, name, description, capabilities }),
    onSuccess: (agent) => {
      toast.success('Agent created');
      qc.invalidateQueries({ queryKey: qk.agents(orgId) });
      done({ kind: 'agent', id: agent.id, orgId });
    },
    onError: (e) => toast.error('Create failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <PanelFormFields onSubmit={() => create.mutate()}>
      <Field id="agent-name" label="Name" required>
        <Input
          id="agent-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          autoFocus
        />
      </Field>
      <Field id="agent-desc" label="Description">
        <Input id="agent-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field
        id="agent-caps"
        label="Capabilities"
        hint="Pick the capabilities this agent can run. You can adjust later."
      >
        <CapabilityMultiSelect value={capabilities} onChange={setCapabilities} />
      </Field>
      <PanelFormActions
        submitLabel="Create agent"
        pending={create.isPending}
        disabled={!name}
        dirty={name !== '' || description !== '' || capabilities.length > 0}
      />
    </PanelFormFields>
  );
}
